import { useEffect, useRef, useState } from "react";
import { Camera, Loader2, Trash2, X } from "lucide-react";
import api from "@/lib/api";

/**
 * Circular avatar for a user + optional inline editor (photo upload, bio).
 * - Small mode: 40px avatar, no editor.
 * - Full mode (`editable`): shows a "Change" button that opens a modal with
 *   upload + bio textarea. RM/self only — backend enforces permissions.
 */
export default function UserAvatar({ userId, name, size = 40, editable = false, bio = "", onSaved }) {
  const [url, setUrl] = useState(null);
  const [tick, setTick] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data } = await api.get(`/users/${userId}/profile-photo-url`);
        if (!cancelled) setUrl(data.url || null);
      } catch { /* silent — fallback to initials */ }
    }
    if (userId) load();
    return () => { cancelled = true; };
  }, [userId, tick]);

  const initials = (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "?";
  const dim = { width: size, height: size };
  const fontSize = Math.round(size * 0.42);

  return (
    <>
      <div className="relative inline-block" data-testid={`user-avatar-${userId}`}>
        {url ? (
          <img src={url} alt={name} style={dim} className="rounded-full object-cover border border-slate-200" />
        ) : (
          <div style={{ ...dim, fontSize }} className="rounded-full bg-[#0D7377] text-white font-semibold flex items-center justify-center">{initials}</div>
        )}
        {editable && (
          <button
            onClick={() => setOpen(true)}
            data-testid={`edit-profile-${userId}`}
            title="Edit profile photo and bio"
            className="absolute -bottom-1 -right-1 bg-white rounded-full p-1 border border-slate-300 shadow-sm hover:bg-slate-50"
          >
            <Camera className="h-3 w-3 text-slate-700" />
          </button>
        )}
      </div>
      {open && (
        <ProfileEditorModal
          userId={userId}
          currentUrl={url}
          currentBio={bio}
          onClose={() => setOpen(false)}
          onSaved={() => { setTick((t) => t + 1); onSaved?.(); setOpen(false); }}
        />
      )}
    </>
  );
}

function ProfileEditorModal({ userId, currentUrl, currentBio, onClose, onSaved }) {
  const [bio, setBio] = useState(currentBio || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(currentUrl);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { setErr("Please choose an image file."); return; }
    if (f.size > 8 * 1024 * 1024) { setErr("File too large — 8MB maximum."); return; }
    setErr("");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove profile photo?")) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/users/${userId}/profile-photo`);
      setPreview(null); setFile(null);
      onSaved?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/users/${userId}/profile-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      if ((bio || "") !== (currentBio || "")) {
        await api.patch(`/users/${userId}/profile`, { bio });
      }
      onSaved?.();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="profile-editor-modal">
      <div className="w-full max-w-md bg-white rounded-lg shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="font-heading font-bold text-slate-900">Edit profile</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="flex flex-col items-center gap-3">
            {preview ? (
              <img src={preview} alt="Preview" className="w-32 h-32 rounded-full object-cover border-2 border-slate-200" data-testid="profile-editor-preview" />
            ) : (
              <div className="w-32 h-32 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-sm">No photo</div>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} className="hidden" data-testid="profile-editor-file" />
            <div className="flex gap-2">
              <button type="button" onClick={() => fileRef.current?.click()} className="text-xs px-3 py-1.5 bg-[#0D7377] hover:bg-[#0A5C5F] text-white rounded font-medium inline-flex items-center gap-1">
                <Camera className="h-3 w-3" /> {preview ? "Change" : "Upload"} photo
              </button>
              {preview && !file && (
                <button type="button" onClick={removePhoto} disabled={busy} className="text-xs px-3 py-1.5 bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 rounded font-medium inline-flex items-center gap-1">
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              )}
            </div>
            <p className="text-[11px] text-slate-500 text-center">Image will be cropped to a circle and used in family notification emails.</p>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">One-line bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={3}
              maxLength={280}
              placeholder="e.g. Enhanced-DBS senior carer with 5 years of dementia support experience."
              data-testid="profile-editor-bio"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
            />
            <p className="text-[11px] text-slate-500 mt-1 text-right">{bio.length}/280</p>
          </div>
          {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{err}</p>}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3 bg-slate-50 rounded-b-lg">
          <button onClick={onClose} disabled={busy} className="px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-white">Cancel</button>
          <button onClick={save} disabled={busy || (!file && bio === (currentBio || ""))} data-testid="profile-editor-save" className="px-3 py-1.5 text-sm bg-[#0D7377] hover:bg-[#0A5C5F] disabled:opacity-50 text-white rounded font-semibold inline-flex items-center gap-1">
            {busy && <Loader2 className="h-3 w-3 animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
